import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { THRESHOLD_OPTIONS, type ApprovalChainFormInput } from '../../model/schema';

type ThresholdType = ApprovalChainFormInput['steps'][number]['approvalThresholdType'];

export interface StepThresholdFieldsProps {
  /** Zero-based position of the step these fields belong to. */
  index: number;
  disabled: boolean;
}

/** A step's approval threshold, and the count it asks for when it is "at least N". */
export function StepThresholdFields(props: StepThresholdFieldsProps) {
  const { t } = useTranslation('approvalChains');
  const { index, disabled } = props;
  const { control, setValue, watch, clearErrors } = useFormContext<ApprovalChainFormInput>();

  const showCount = watch(`steps.${index}.approvalThresholdType`) === 'at_least_n';
  const namesRole = !!watch(`steps.${index}.approverRole`);

  const handleThresholdChange = (next: string) => {
    setValue(`steps.${index}.approvalThresholdType`, next as ThresholdType, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (next === 'at_least_n') {
      const current = watch(`steps.${index}.approvalThresholdCount`);
      if (current === undefined) {
        setValue(`steps.${index}.approvalThresholdCount`, 1, { shouldDirty: true });
      }
    } else {
      setValue(`steps.${index}.approvalThresholdCount`, undefined, { shouldDirty: true });
      clearErrors(`steps.${index}.approvalThresholdCount`);
    }
  };

  return (
    <FormField
      control={control}
      name={`steps.${index}.approvalThresholdCount`}
      render={({ field }) => (
        // The count's item spans the whole row, so its message runs under both fields instead of
        // wrapping in the count's narrow column; the input keeps its red border and description.
        <FormItem>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <FormField
              control={control}
              name={`steps.${index}.approvalThresholdType`}
              render={({ field: typeField }) => (
                <FormItem className="flex-1">
                  <FormLabel>
                    {t('manageApprovalChains.approvalChainStepRow.approvalThreshold')}
                  </FormLabel>
                  <Select
                    value={typeField.value}
                    onValueChange={handleThresholdChange}
                    disabled={disabled}
                  >
                    <FormControl>
                      <SelectTrigger size="lg" data-testid="approval-chain-step-threshold">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {THRESHOLD_OPTIONS.map((option) => {
                        const closedByRole = namesRole && option.value === 'all';
                        return (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                            disabled={closedByRole}
                          >
                            <div className="flex flex-col">
                              <span>{t(option.labelKey)}</span>
                              {closedByRole && (
                                <span className="text-xs text-text-secondary">
                                  {t('manageApprovalChains.schema.allApproversNotWithRole')}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showCount && (
              <div className="grid w-full gap-2 sm:w-40">
                <FormLabel>
                  {t('manageApprovalChains.approvalChainStepRow.requiredApprovers')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    size="lg"
                    min={1}
                    inputMode="numeric"
                    disabled={disabled}
                    value={field.value ?? ''}
                    onChange={(event) => {
                      const raw = event.target.value;
                      field.onChange(raw === '' ? undefined : Number(raw));
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    data-testid="approval-chain-step-threshold-count"
                  />
                </FormControl>
              </div>
            )}
          </div>
          {showCount && <FormMessage />}
        </FormItem>
      )}
    />
  );
}
